import React, {FC} from 'react';
import {IOrganization, TUsageType} from '@verdocs/js-sdk';
import {ChartBarIcon, DocumentDuplicateIcon, EnvelopeIcon, ShieldCheckIcon} from '@heroicons/react/24/outline';
import {OrgAvatar} from './OrgAvatar';

type TUsageCounts = Partial<Record<TUsageType, number>>;

interface UsageSectionProps {
  org: IOrganization;
  usage?: TUsageCounts;
  badge?: string;
  badgeTone?: 'indigo' | 'gray';
}

interface IUsageGroup {
  label: string;
  icon: FC<{className?: string}>;
  types: {id: TUsageType; label: string}[];
}

const USAGE_GROUPS: IUsageGroup[] = [
  {
    label: 'Envelope Activity',
    icon: EnvelopeIcon,
    types: [
      {id: 'envelope', label: 'Created'},
      {id: 'envelope_completed', label: 'Completed'},
      {id: 'envelope_canceled', label: 'Canceled'},
      {id: 'envelope_expired', label: 'Expired'},
    ],
  },
  {
    label: 'Recipient Verification',
    icon: ShieldCheckIcon,
    types: [
      {id: 'auth_email', label: 'Email OTP'},
      {id: 'auth_sms', label: 'SMS OTP'},
      {id: 'auth_kba', label: 'Knowledge-Based'},
      {id: 'auth_id', label: 'ID Verification'},
      {id: 'auth_passcode', label: 'Passcode'},
    ],
  },
  {
    label: 'Templates & Messaging',
    icon: DocumentDuplicateIcon,
    types: [
      {id: 'template', label: 'Templates Created'},
      {id: 'sms_invite', label: 'SMS Invites'},
    ],
  },
];

const KNOWN_TYPES = new Set<string>(USAGE_GROUPS.flatMap((group) => group.types.map((type) => type.id)));

// Fallback labeling so new usage types reported by the API still display before we map them
const prettifyUsageType = (id: string) => id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const SummaryTile: FC<{label: string; value: number; hint?: string}> = ({label, value, hint}) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3" title={hint}>
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${value > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{value}</div>
  </div>
);

const UsageRow: FC<{label: string; value: number}> = ({label, value}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-600">{label}</span>
    <span className={value > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'}>{value}</span>
  </div>
);

export const UsageSection: FC<UsageSectionProps> = ({org, usage, badge, badgeTone = 'gray'}) => {
  const count = (id: TUsageType) => usage?.[id] ?? 0;
  const verifications = count('auth_email') + count('auth_sms') + count('auth_kba') + count('auth_id') + count('auth_passcode');
  const hasActivity = Object.values(usage || {}).some((value) => value > 0);

  const otherTypes = Object.keys(usage || {}).filter((id) => !KNOWN_TYPES.has(id));
  const groups: IUsageGroup[] = otherTypes.length
    ? [
        ...USAGE_GROUPS,
        {label: 'Other', icon: ChartBarIcon, types: otherTypes.map((id) => ({id: id as TUsageType, label: prettifyUsageType(id)}))},
      ]
    : USAGE_GROUPS;

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <header className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
        <OrgAvatar org={org} size="sm" />
        <h3 className="flex-1 truncate text-[15px] font-semibold text-gray-900">{org.name}</h3>
        {badge && (
          <span
            className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
              badgeTone === 'indigo' ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-500'
            }`}>
            {badge}
          </span>
        )}
      </header>

      <div className="p-5">
        <div className="grid grid-cols-2 gap-3 tablet:grid-cols-4">
          <SummaryTile label="Envelopes Created" value={count('envelope')} />
          <SummaryTile label="Envelopes Completed" value={count('envelope_completed')} />
          <SummaryTile label="Templates Created" value={count('template')} />
          <SummaryTile label="Verifications" value={verifications} hint="Email, SMS, KBA, ID, and passcode verifications" />
        </div>

        {hasActivity ? (
          <div className="mt-4 grid grid-cols-1 items-start gap-3 tablet:grid-cols-3">
            {groups.map((group) => {
              const GroupIcon = group.icon;
              return (
                <div key={group.label} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="mb-3 flex items-center gap-1.5">
                    <GroupIcon className="h-4 w-4 text-indigo-500" />
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</h4>
                  </div>
                  <div className="flex flex-col gap-2">
                    {group.types.map((type) => (
                      <UsageRow key={type.id} label={type.label} value={count(type.id)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 py-10 text-center">
            <ChartBarIcon className="mb-3 h-8 w-8 text-gray-300" />
            <p className="text-sm font-medium text-gray-600">No activity in this period</p>
            <p className="mt-1 max-w-[400px] text-xs text-gray-400">
              Usage appears here as {org.name} creates templates and sends envelopes.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
